import type { DateTime } from "luxon";
import { formatShiftHours } from "@/components/AddNurse/addStaffUtils";
import { formatPersonFullName } from "@/utils/formatters";
import type { Nurse, NurseData, NurseInShift, PanelShiftData, WeekData } from "@/utils/models";
import { getEmploymentTypeDisplayName } from "@/utils/utilFunctions";
import type { RemoveStaffShift, RemoveStaffShiftPlacement, RemoveStaffWindows } from "./removeStaffWindows";

/** A shift on the date as the grid holds it: the row's `shiftId` is its identity (`rowKey`), so one nurse can be listed twice. */
export type RemoveStaffRow = NurseData & { shiftId: string; rowKey: string };

/** Where a shift sits in the loaded week: the shift itself and its legacy Day (0) / Night (1) bucket. */
export type RemoveStaffShiftRef = { shift: NurseInShift; shiftIndex: number };

/** The loaded shift with this id on `dayIndex`, and the bucket holding it — or `null` when it isn't loaded. */
export const findShiftOnDay = (week: WeekData, dayIndex: number, shiftId: string): RemoveStaffShiftRef | null => {
	for (const [shiftIndex, bucket] of week.weeklyShifts.entries()) {
		const shift = bucket.days[dayIndex]?.nurses.find((nurse) => nurse.id === shiftId);
		if (shift) return { shift, shiftIndex };
	}
	return null;
};

/** The listed shift's hours: the loaded shift's (department time), else the list's own, read in the department's zone. */
export const toPlacementShift = (
	row: Pick<NurseData, "shiftTimes">,
	shiftId: string,
	ref: RemoveStaffShiftRef | null,
	timezone: string,
): RemoveStaffShift | null => {
	if (ref) return { id: shiftId, startTime: ref.shift.startTime, endTime: ref.shift.endTime };
	if (!row.shiftTimes) return null;
	return { id: shiftId, startTime: row.shiftTimes.start.setZone(timezone), endTime: row.shiftTimes.end.setZone(timezone) };
};

/**
 * The week strip with the date's cell describing THIS row's shift: its own bucket's sun / moon and its own
 * recurring marker. `generateShiftData` answers per nurse — the first bucket she is in, and "recurring" if ANY
 * of her shifts that day is — which would mark a one-off extra shift as recurring beside her template one.
 */
export const withOwnDayCell = (shifts: readonly PanelShiftData[], dayIndex: number, ref: RemoveStaffShiftRef, bucketType: string): PanelShiftData[] =>
	shifts.map((cell, index) =>
		index === dayIndex
			? { ...cell, shiftType: bucketType.toLowerCase(), isRecurringShift: ref.shift.recurringShiftId != null, isFromMasterSchedule: false }
			: cell,
	);

type RowContext = {
	week: WeekData;
	dayIndex: number;
	placementByShiftId: ReadonlyMap<string, RemoveStaffShiftPlacement>;
	getNurseById: (id: number) => Nurse | undefined;
	/** The nurse's week strip for this date (`generateShiftData`). */
	buildWeekStrip: (nurse: Nurse) => PanelShiftData[];
	jobNameOf: (jobId: string) => string | undefined;
	callOffsOf: (nurseId: number) => NonNullable<NurseData["lastWeekCalloffs"]>;
	timezone: string;
	/** The department's name-order flag (`useLastNameFirst()`). */
	lastNameFirst: boolean;
};

/** Hours under the name when they aren't the window's — "8:00 AM–3:00 PM" in a 7–3 band, and always under Other. */
const captionFor = (placement: RemoveStaffShiftPlacement | undefined, times: RemoveStaffShift | null): string | undefined =>
	placement?.isCustomHours && times ? formatShiftHours({ start: times.startTime, end: times.endTime }) : undefined;

const nurseFields = (nurse: Nurse | undefined, weekStrip: PanelShiftData[] | undefined, lastNameFirst: boolean): Partial<NurseData> =>
	nurse && weekStrip
		? {
				name: formatPersonFullName(nurse, lastNameFirst),
				shifts: weekStrip,
				nurseType: getEmploymentTypeDisplayName(nurse.employmentType),
				...(nurse.nursingStartingDate && { nurseStartDate: nurse.nursingStartingDate }),
			}
		: {};

/** One listed shift → its grid row. */
const toRow = (listed: NurseData & { shiftId: string }, ctx: RowContext): RemoveStaffRow => {
	const ref = findShiftOnDay(ctx.week, ctx.dayIndex, listed.shiftId);
	const nurse = ctx.getNurseById(listed.id);
	const bucketType = ref ? ctx.week.weeklyShifts[ref.shiftIndex].type : null;
	const baseStrip = nurse ? ctx.buildWeekStrip(nurse) : undefined;
	const weekStrip = baseStrip && ref && bucketType ? withOwnDayCell(baseStrip, ctx.dayIndex, ref, bucketType) : baseStrip;
	const jobName = listed.jobId ? ctx.jobNameOf(listed.jobId) : undefined;
	const times = toPlacementShift(listed, listed.shiftId, ref, ctx.timezone);
	const nameCaption = captionFor(ctx.placementByShiftId.get(listed.shiftId), times);
	return {
		...listed,
		rowKey: listed.shiftId,
		shiftStartTime: listed.shiftTimes?.start,
		shiftEndTime: listed.shiftTimes?.end,
		...(jobName ? { jobName } : {}),
		...nurseFields(nurse, weekStrip, ctx.lastNameFirst),
		...(nameCaption ? { nameCaption } : {}),
		lastWeekCalloffs: ctx.callOffsOf(listed.id),
		selectable: true,
	};
};

/** Only shifts the list identifies can be removed; the endpoint always sends the id, so none is expected to drop. */
export const hasShiftId = (row: NurseData): row is NurseData & { shiftId: string } => typeof row.shiftId === "string" && row.shiftId.length > 0;

/**
 * The Remove Staff grid rows: one per SHIFT on the date (a nurse working two is listed twice), each with its own
 * bucket and recurring marker on the date's cell and its custom hours under the name.
 */
export const buildRemoveStaffRows = (listed: readonly NurseData[], ctx: RowContext): RemoveStaffRow[] =>
	listed.filter(hasShiftId).map((row) => toRow(row, ctx));

/** The list's rows shaped for the window derivation: one placement shift per listed shift that has hours. */
export const collectPlacementShifts = (listed: readonly NurseData[], week: WeekData, dayIndex: number, timezone: string): RemoveStaffShift[] =>
	listed
		.filter(hasShiftId)
		.map((row) => toPlacementShift(row, row.shiftId, findShiftOnDay(week, dayIndex, row.shiftId), timezone))
		.filter((shift): shift is RemoveStaffShift => shift !== null);

/**
 * The windows with every row counted: a listed shift with no hours to place (`collectPlacementShifts` skips it)
 * is listed under Other, so it is counted under Other too — the Other pill shows when such a row exists, and the
 * pill counts always add up to "All shifts".
 */
export const countUnplacedAsOther = (windows: RemoveStaffWindows, rows: readonly RemoveStaffRow[]): RemoveStaffWindows => {
	const unplacedCount = rows.filter((row) => !windows.placementByShiftId.has(row.shiftId)).length;
	return unplacedCount === 0 ? windows : { ...windows, otherCount: windows.otherCount + unplacedCount };
};

/** A hospital demo's day has no remove list; its rows are the loaded shifts themselves, as before. */
export const buildDemoListedShifts = (week: WeekData, dayIndex: number, getNurseById: (id: number) => Nurse | undefined, lastNameFirst: boolean): NurseData[] =>
	week.weeklyShifts.flatMap((bucket) =>
		bucket.days[dayIndex].nurses.flatMap((shift): NurseData[] => {
			const nurse = getNurseById(shift.nurseId);
			if (!nurse) return [];
			// Only what the row builder reads; the demo list has no cost, overtime or call-off figures.
			const listed: NurseData = {
				id: shift.nurseId,
				shiftId: shift.id,
				name: formatPersonFullName(nurse, lastNameFirst),
				role: nurse.role,
				trainingStatus: nurse.trainingStatus,
				shifts: [],
				shiftTimes: { start: shift.startTime, end: shift.endTime },
				selectable: true,
			};
			return [listed];
		}),
	);

/** The staffing date of a loaded day (every bucket carries the same date). */
export const staffingDateOf = (week: WeekData, dayIndex: number): DateTime => week.weeklyShifts[0].days[dayIndex].date;
